import { EmploymentField, useEmploymentSelection } from './EmploymentField'
import { useState } from 'react'
import { Text, XStack, YStack } from 'tamagui'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useApp } from '../../../i18n'
import { api } from '../../../api/client'
import { Avatar, Button, FormInput, FormTextArea, Icon, IconButton, OptionSelect, PressCard } from '../../../ui'
import { pickFileWeb } from '../../../lib/files'
import type { Profile } from '../../../lib/types'
import { useMe } from '../../users/useUsers'
import { useUserSearch } from '../../users/useUserSearch'
import { Field, FormActions, MISSING_WORKPLACE, isWeb } from './shared'

// ── Free-form document (произвольная форма + свой маршрут) ─────────────────────
/**
 * Документ произвольной формы: тема + текст + вложения, а маршрут согласования собирает сам
 * инициатор. Режим выбирает он же: ПО ОЧЕРЕДИ (в порядке добавления) или ОДНОВРЕМЕННО (все
 * получают документ сразу, круг закрывает последний согласовавший).
 * Порядок меняется удалением и повторным добавлением: отдельного drag-n-drop нет намеренно
 * (маршруты короткие, а перетаскивание в списке внутри скролла — источник багов на вебе).
 */
export function FreeformForm({ onDone }: { onDone: () => void }) {
  const employment = useEmploymentSelection()
  const { t } = useApp()
  const qc = useQueryClient()
  const me = useMe()
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [route, setRoute] = useState<Profile[]>([])
  const [parallel, setParallel] = useState(false)
  const [files, setFiles] = useState<File[]>([])

  // Пикер согласующих: справочник людей. Ищем только с 2 символов — иначе первый же фокус
  // тянет весь штат.
  const { q: search, setQ: setSearch, hits } = useUserSearch()
  const chosen = new Set(route.map((p) => p.id))
  const found = (hits ?? [])
    .filter((p) => p.id !== me.data?.id && !chosen.has(p.id))
    .slice(0, 8)

  const create = useMutation({
    mutationFn: async () => {
      // Файлы грузим как edo_doc и шлём их id — тот же контракт, что у справки отцовского.
      const uploadIds: string[] = []
      for (const file of files) {
        const form = new FormData()
        form.append('file', file as unknown as Blob)
        uploadIds.push((await api.uploadEdoDoc(form)).id)
      }
      return api.createFreeform({
        employmentId: employment.employmentId,
        title: title.trim(),
        body: body.trim(),
        approvers: route.map((p) => p.id),
        parallel,
        uploadIds: uploadIds.length ? uploadIds : undefined,
      })
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['edo-requests'] })
      onDone()
    },
  })

  const missing = [
    !employment.ready && t(MISSING_WORKPLACE),
    title.trim() === '' && t({ ru: 'Тема документа', en: 'Subject' }),
    body.trim() === '' && t({ ru: 'Текст документа', en: 'Document text' }),
    route.length === 0 && t({ ru: 'Маршрут согласования', en: 'Approval route' }),
  ].filter((x): x is string => !!x)
  const ok = missing.length === 0 && !create.isPending
  const submit = () => ok && create.mutate()

  return (
    <YStack gap={14}>
      <EmploymentField selection={employment} />
      <Field label={t({ ru: 'Тема документа', en: 'Subject' })} required>
        <FormInput
          value={title}
          onChangeText={setTitle}
          maxLength={200}
          placeholder={t({ ru: 'Например: служебная записка о закупке', en: 'e.g. purchase memo' })}
        />
      </Field>

      <Field label={t({ ru: 'Текст документа', en: 'Document text' })} required>
        <FormTextArea
          value={body}
          onChangeText={setBody}
          maxLength={10000}
          placeholder={t({ ru: 'Изложите суть документа', en: 'Describe the matter' })}
          minHeight={140}
        />
      </Field>

      <Field label={t({ ru: 'Порядок согласования', en: 'Approval order' })} required>
        <YStack gap={8}>
          <OptionSelect
            options={[
              { value: 'seq', label: t({ ru: 'По очереди', en: 'One by one', az: 'Növbə ilə' }) },
              { value: 'par', label: t({ ru: 'Одновременно', en: 'All at once', az: 'Eyni vaxtda' }) },
            ]}
            value={[parallel ? 'par' : 'seq']}
            onChange={(next) => setParallel(next[0] === 'par')}
          />
          <Text fontSize={12} color="$muted">
            {parallel
              ? t({
                  ru: 'Все согласующие получат документ сразу. Он согласован, когда согласовали все; первое отклонение или возврат на доработку останавливают круг.',
                  en: 'Everyone receives the document at once. Approved once all have approved; the first rejection or rework request stops the round.',
                })
              : t({
                  ru: 'Согласующие получат документ по очереди, в порядке списка.',
                  en: 'Approvers receive the document one by one, in the listed order.',
                })}
          </Text>
        </YStack>
      </Field>

      <Field label={t({ ru: 'Маршрут согласования', en: 'Approval route' })} required>
        <YStack gap={10}>
          {route.length === 0 ? (
            <Text fontSize={12} color="$muted">
              {t({
                ru: 'Добавьте согласующих.',
                en: 'Add approvers.',
              })}
            </Text>
          ) : (
            route.map((p, i) => (
              <XStack key={p.id} alignItems="center" gap={10}>
                <Text fontSize={12} fontWeight="700" color="$primary" width={18}>
                  {/* Нумерация — только у последовательного маршрута: в параллельном круге
                      порядок ничего не значит и цифры врали бы про очерёдность. */}
                  {parallel ? '·' : `${i + 1}.`}
                </Text>
                <Avatar name={p.fullName} uri={p.avatarUrl} size={28} />
                <YStack flex={1}>
                  <Text fontSize={13} color="$color">{p.fullName}</Text>
                  {!!p.jobTitle && (
                    <Text fontSize={11} color="$muted">{p.jobTitle}</Text>
                  )}
                </YStack>
                <IconButton onPress={() => setRoute(route.filter((x) => x.id !== p.id))}>
                  <Icon name="x" size={15} color="$muted" />
                </IconButton>
              </XStack>
            ))
          )}

          <FormInput
            value={search}
            onChangeText={setSearch}
            placeholder={t({ ru: 'Поиск сотрудника (от 2 символов)', en: 'Search employee (2+ chars)' })}
          />
          {found.map((p) => (
            <PressCard
              key={p.id}
              padding={9}
              onPress={() => {
                setRoute([...route, p])
                setSearch('')
              }}
            >
              <XStack alignItems="center" gap={10}>
                <Avatar name={p.fullName} uri={p.avatarUrl} size={28} />
                <YStack flex={1}>
                  <Text fontSize={13} color="$color">{p.fullName}</Text>
                  {!!p.jobTitle && (
                    <Text fontSize={11} color="$muted">{p.jobTitle}</Text>
                  )}
                </YStack>
                <Icon name="plus" size={15} color="$primary" />
              </XStack>
            </PressCard>
          ))}
        </YStack>
      </Field>

      <Field label={t({ ru: 'Вложения', en: 'Attachments' })}>
        {isWeb ? (
          <YStack gap={8}>
            {files.map((f) => (
              <XStack key={f.name} alignItems="center" gap={8}>
                <Icon name="paperclip" size={14} color="$muted" />
                <Text fontSize={12} color="$color" flex={1}>{f.name}</Text>
                <IconButton onPress={() => setFiles(files.filter((x) => x !== f))}>
                  <Icon name="x" size={14} color="$muted" />
                </IconButton>
              </XStack>
            ))}
            <Button
              variant="default"
              block
              icon={<Icon name="paperclip" size={15} color="$muted" />}
              onPress={async () => {
                const f = await pickFileWeb('.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png')
                if (f) setFiles((prev) => [...prev, f])
              }}
            >
              {t({ ru: 'Прикрепить файл', en: 'Attach a file' })}
            </Button>
          </YStack>
        ) : (
          <Text fontSize={12} color="$muted">
            {t({
              ru: 'Прикрепление файлов доступно в веб-версии портала',
              en: 'File upload is available in the web version',
            })}
          </Text>
        )}
      </Field>

      <FormActions onCancel={onDone} onSubmit={submit} ok={ok} m={create} missing={missing} />
    </YStack>
  )
}
